import { create } from 'zustand';
import type { User, UserSettings } from '@mindrift/shared';
import { api, loadStoredRefresh, setAuthTokens } from '../api/client';

interface AuthState {
  user: User | null;
  settings: UserSettings | null;
  loading: boolean;
  initialized: boolean;
  init: () => Promise<void>;
  setSession: (user: User, tokens: { accessToken: string; refreshToken: string }) => void;
  setUser: (user: User) => void;
  setSettings: (settings: UserSettings) => void;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  settings: null,
  loading: false,
  initialized: false,
  async init() {
    if (get().initialized) return;
    set({ loading: true });
    try {
      const refresh = loadStoredRefresh();
      if (!refresh) {
        set({ initialized: true, loading: false });
        return;
      }
      try {
        const res = await api.auth.me();
        set({ user: res.user, initialized: true, loading: false });
        try {
          const settings = await api.settings.get();
          set({ settings });
        } catch {
          // settings may not exist yet
        }
      } catch {
        set({ initialized: true, loading: false });
      }
    } catch {
      set({ initialized: true, loading: false });
    }
  },
  setSession(user, tokens) {
    setAuthTokens(tokens);
    set({ user, initialized: true });
  },
  setUser(user) {
    set({ user });
  },
  setSettings(settings) {
    set({ settings });
  },
  async logout() {
    try {
      await api.auth.logout();
    } catch {
      // ignore
    }
    setAuthTokens(null);
    set({ user: null, settings: null });
  },
}));
